import { useMemo, useState } from 'react'
import { Check, ChevronDown, Filter } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { DateTimePicker } from '@/components/ui/date-time-picker'

export type TransactionsFilters = {
  channel?: 'all' | 'pos' | 'mobile'
  status?:
    | 'all'
    | 'COMPLETED'
    | 'VOIDED'
    | 'REFUNDED'
    | 'CANCELLED'
    | 'PENDING'
    | 'AUTHORIZED'
    | 'CAPTURED'
  payment?: 'all' | 'cash' | 'card' | 'voucher'
  range?: 'all' | 'today' | '24h' | '7d' | 'custom'
  fromDate?: string
  fromTime?: string
  toDate?: string
  toTime?: string
}

interface Props {
  value: TransactionsFilters
  onChange: (value: TransactionsFilters) => void
  onClear: () => void
}

export function TransactionsFiltersPopover({
  value,
  onChange,
  onClear,
}: Props) {
  const { t: tTransactions } = useTranslation('transactions')
  const [open, setOpen] = useState(false)
  const filterCount = useMemo(() => {
    let n = 0
    if (value.channel && value.channel !== 'all') n += 1
    if (value.status && value.status !== 'all') n += 1
    if (value.payment && value.payment !== 'all') n += 1
    if (value.range && value.range !== 'all') n += 1
    return n
  }, [value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Filter className="w-4 h-4 mr-2" />
          {tTransactions('filters.label')}
          {filterCount > 0 ? ` (${filterCount})` : ''}
          <ChevronDown className="w-4 h-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[640px] max-h-[70vh] overflow-auto p-0"
        align="end"
      >
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-1">
              <Label>{tTransactions('filters.channel')}</Label>
              <div className="border rounded-md">
                <Command>
                  <CommandInput
                    placeholder={tTransactions('filters.channelPlaceholder')}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {tTransactions('filters.channelEmpty')}
                    </CommandEmpty>
                    <CommandGroup>
                      {(['all', 'pos', 'mobile'] as const).map((c) => (
                        <CommandItem
                          key={c}
                          onSelect={() => onChange({ ...value, channel: c })}
                        >
                          <span className="flex items-center gap-2">
                            {value.channel === c ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <span className="w-4 h-4" />
                            )}
                            {tTransactions(`values.channel.${c}`)}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
            </div>

            <div className="space-y-2 md:col-span-1">
              <Label>{tTransactions('filters.status')}</Label>
              <div className="border rounded-md">
                <Command>
                  <CommandInput
                    placeholder={tTransactions('filters.statusPlaceholder')}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {tTransactions('filters.statusEmpty')}
                    </CommandEmpty>
                    <CommandGroup>
                      {(
                        [
                          'all',
                          'COMPLETED',
                          'VOIDED',
                          'REFUNDED',
                          'CANCELLED',
                          'PENDING',
                          'AUTHORIZED',
                          'CAPTURED',
                        ] as const
                      ).map((s) => (
                        <CommandItem
                          key={s}
                          onSelect={() => onChange({ ...value, status: s })}
                        >
                          <span className="flex items-center gap-2">
                            {value.status === s ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <span className="w-4 h-4" />
                            )}
                            {tTransactions(`values.status.${s}`)}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
            </div>

            <div className="space-y-2 md:col-span-1">
              <Label>{tTransactions('filters.payment')}</Label>
              <div className="border rounded-md">
                <Command>
                  <CommandInput
                    placeholder={tTransactions('filters.paymentPlaceholder')}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {tTransactions('filters.paymentEmpty')}
                    </CommandEmpty>
                    <CommandGroup>
                      {(['all', 'cash', 'card', 'voucher'] as const).map(
                        (p) => (
                          <CommandItem
                            key={p}
                            onSelect={() => onChange({ ...value, payment: p })}
                          >
                            <span className="flex items-center gap-2">
                              {value.payment === p ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <span className="w-4 h-4" />
                              )}
                              {tTransactions(`values.payment.${p}`)}
                            </span>
                          </CommandItem>
                        ),
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </div>
            </div>

            <div className="space-y-2 md:col-span-1">
              <Label>{tTransactions('filters.range')}</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {(['all', 'today', '24h', '7d', 'custom'] as const).map((r) => (
                  <Button
                    key={r}
                    variant={value.range === r ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onChange({ ...value, range: r })}
                  >
                    {tTransactions(`filters.rangeOptions.${r}`)}
                  </Button>
                ))}
              </div>
              {value.range === 'custom' && (
                <div className="space-y-3 mt-1">
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {tTransactions('filters.from')}
                    </Label>
                    <DateTimePicker
                      date={value.fromDate}
                      time={value.fromTime}
                      onDateChange={(d) =>
                        onChange({ ...value, range: 'custom', fromDate: d })
                      }
                      onTimeChange={(t) =>
                        onChange({ ...value, range: 'custom', fromTime: t })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">
                      {tTransactions('filters.to')}
                    </Label>
                    <DateTimePicker
                      date={value.toDate}
                      time={value.toTime}
                      onDateChange={(d) =>
                        onChange({ ...value, range: 'custom', toDate: d })
                      }
                      onTimeChange={(t) =>
                        onChange({ ...value, range: 'custom', toTime: t })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between pt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onClear()
                setOpen(false)
              }}
            >
              {tTransactions('filters.clear')}
            </Button>
            <Button size="sm" onClick={() => setOpen(false)}>
              {tTransactions('filters.apply')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface ChipsProps {
  restaurantName?: string
  channel?: string
  status?: string
  payment?: string
  range?: string
  onClear: () => void
  onClearField: (field: keyof TransactionsFilters) => void
}

export function TransactionsActiveChips({
  restaurantName,
  channel,
  status,
  payment,
  range,
  onClear,
  onClearField,
}: ChipsProps) {
  const { t: tTransactions } = useTranslation('transactions')
  const hasAny =
    !!restaurantName ||
    (channel && channel !== 'all') ||
    (status && status !== 'all') ||
    (payment && payment !== 'all') ||
    (range && range !== 'all')
  if (!hasAny) return null
  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      {restaurantName && (
        <span className="text-xs text-muted-foreground">
          {tTransactions('filters.chips.restaurant')}: {restaurantName}
        </span>
      )}
      {channel && channel !== 'all' && (
        <span className="text-xs bg-muted px-2 py-1 rounded">
          {tTransactions('filters.chips.channel')}:{' '}
          {tTransactions(`values.channel.${channel}`)}{' '}
          <button
            className="ml-1 underline"
            onClick={() => onClearField('channel')}
          >
            {tTransactions('filters.chips.clear')}
          </button>
        </span>
      )}
      {status && status !== 'all' && (
        <span className="text-xs bg-muted px-2 py-1 rounded">
          {tTransactions('filters.chips.status')}:{' '}
          {tTransactions(`values.status.${status}`)}{' '}
          <button
            className="ml-1 underline"
            onClick={() => onClearField('status')}
          >
            {tTransactions('filters.chips.clear')}
          </button>
        </span>
      )}
      {payment && payment !== 'all' && (
        <span className="text-xs bg-muted px-2 py-1 rounded">
          {tTransactions('filters.chips.payment')}:{' '}
          {tTransactions(`values.payment.${payment}`)}{' '}
          <button
            className="ml-1 underline"
            onClick={() => onClearField('payment')}
          >
            {tTransactions('filters.chips.clear')}
          </button>
        </span>
      )}
      {range && range !== 'all' && (
        <span className="text-xs bg-muted px-2 py-1 rounded">
          {tTransactions('filters.chips.range')}:{' '}
          {tTransactions(`filters.rangeOptions.${range}`)}{' '}
          <button
            className="ml-1 underline"
            onClick={() => onClearField('range')}
          >
            {tTransactions('filters.chips.clear')}
          </button>
        </span>
      )}
      <button
        className="text-xs text-muted-foreground underline ml-2"
        onClick={onClear}
      >
        {tTransactions('filters.chips.clearAll')}
      </button>
    </div>
  )
}
